import React, { useRef, useState } from "react";

import { forgetNavConfig, saveNavConfig, useNavConfig } from "./navEntries";

/**
 * The rail, as a list you can put in order.
 * ---------------------------------------------------------------------------
 * Drag a row to move it, uncheck one to take it off the rail. Both are written
 * as they are done, so the rail behind this window reorders while the row is
 * still in hand - which is the only way to tell whether the order is the one
 * you wanted.
 *
 * Dragged with the browser's own drag and drop rather than a library: eleven
 * rows in a column need a grab, a position and a drop, and that is all this
 * is.
 * ---------------------------------------------------------------------------
 */
export default function NavEntriesSettings({ isDarkMode }) {
  const [list, setList] = useNavConfig();
  const [over, setOver] = useState(-1);
  const from = useRef(-1);

  const ink = isDarkMode ? "rgba(245,239,230,0.9)" : "rgba(17,17,17,0.9)";
  const dim = isDarkMode ? "rgba(245,239,230,0.5)" : "rgba(17,17,17,0.55)";
  const hair = isDarkMode ? "rgba(245,239,230,0.12)" : "rgba(17,17,17,0.12)";
  const raise = isDarkMode ? "rgba(245,239,230,0.06)" : "rgba(17,17,17,0.04)";

  const commit = (next) => { setList(next); saveNavConfig(next); };

  const move = (a, b) => {
    if (a < 0 || b < 0 || a === b) return;
    const next = list.slice();
    const [row] = next.splice(a, 1);
    next.splice(b, 0, row);
    commit(next);
  };

  const shown = list.filter(e => e.on !== false).length;

  return (
    <div style={{ maxWidth: 560, color: ink, fontFamily: "Inter, system-ui, sans-serif" }}>
      <p style={{ margin: "0 0 16px", fontSize: 12.5, lineHeight: 1.5, color: dim }}>
        Drag to reorder, type over a name to change it, uncheck to take an entry
        off the rail. {shown} of {list.length} shown — a hidden page is still at
        its own address.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {list.map((e, i) => (
          <div
            key={e.id}
            draggable
            onDragStart={(ev) => { from.current = i; ev.dataTransfer.effectAllowed = "move"; }}
            onDragOver={(ev) => { ev.preventDefault(); setOver(i); }}
            onDragLeave={() => setOver(o => (o === i ? -1 : o))}
            onDrop={(ev) => { ev.preventDefault(); move(from.current, i); from.current = -1; setOver(-1); }}
            onDragEnd={() => { from.current = -1; setOver(-1); }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "9px 12px",
              borderRadius: 9,
              border: `1px solid ${over === i ? "rgb(246,64,13)" : "transparent"}`,
              background: over === i ? raise : "transparent",
              cursor: "grab",
            }}
          >
            {/* the grip: what says a row can be taken hold of */}
            <span aria-hidden="true" style={{ display: "inline-flex", color: dim, opacity: 0.7 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
                <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
                <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
              </svg>
            </span>

            <input
              type="checkbox"
              checked={e.on !== false}
              aria-label={`Show ${e.name || e.label}`}
              onChange={() => commit(list.map((x, j) => (j === i ? { ...x, on: !(x.on !== false) } : x)))}
              style={{ accentColor: "rgb(246,64,13)", width: 15, height: 15, flex: "0 0 auto", cursor: "pointer" }}
            />

            {/* What it is called. Typed over, the rail takes the new name; left
                empty, it goes back to the one the product ships with - which is
                shown as the placeholder, so the original is never lost. */}
            <input
              type="text"
              value={e.name || ""}
              placeholder={e.label}
              aria-label={`What ${e.label} is called`}
              maxLength={28}
              onChange={(ev) => commit(list.map((x, j) => (j === i ? { ...x, name: ev.target.value } : x)))}
              onDragStart={(ev) => ev.preventDefault()}
              style={{
                flex: "1 1 auto",
                minWidth: 0,
                height: 28,
                padding: "0 9px",
                borderRadius: 7,
                border: `1px solid ${hair}`,
                background: "transparent",
                color: e.on === false ? dim : ink,
                fontFamily: "inherit",
                fontSize: 13,
                outline: "none",
                cursor: "text",
              }}
            />

            <span style={{
              flex: "0 0 auto",
              fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
              fontSize: 10, color: dim, opacity: 0.8,
            }}>
              {e.id.replace(/^\.\//, "/")}
            </span>

            {/* ...and the keyboard's way to move it, since dragging is not one */}
            <span style={{ display: "inline-flex", gap: 2 }}>
              {[["↑", i - 1, i > 0], ["↓", i + 1, i < list.length - 1]].map(([mark, to, can]) => (
                <button
                  key={mark}
                  type="button"
                  disabled={!can}
                  onClick={() => move(i, to)}
                  aria-label={mark === "↑" ? `Move ${e.label} up` : `Move ${e.label} down`}
                  style={{
                    width: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center",
                    padding: 0, borderRadius: 6, border: `1px solid ${hair}`, background: "transparent",
                    color: can ? ink : dim, opacity: can ? 1 : 0.35,
                    fontSize: 11, cursor: can ? "pointer" : "default",
                  }}
                >
                  {mark}
                </button>
              ))}
            </span>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => forgetNavConfig()}
        style={{
          marginTop: 18, height: 30, padding: "0 14px", borderRadius: 8,
          border: `1px solid ${hair}`, background: "transparent", color: dim,
          fontFamily: "inherit", fontSize: 12, cursor: "pointer",
        }}
      >
        Back to the names and order it ships with
      </button>
    </div>
  );
}
